import type {
  ModuleId,
  SearchEntity,
  SearchEntityDestination,
  SearchModule,
} from "./search-modules";
import type { SearchResultGroup } from "./global-search.types";

type RecentEntitySelection = {
  kind: "entity";
  moduleId: ModuleId;
  entityId: string;
  label: string;
};

type FrequentEntitySelection = {
  module: SearchModule;
  entity: SearchEntity;
  count: number;
};

type BuildGlobalSearchGroupsInput = {
  branchId: string;
  selectedModule: SearchModule | null;
  selectedEntity: SearchEntity | null;
  frequentModules: SearchModule[];
  frequentEntities: FrequentEntitySelection[];
  visibleModules: SearchModule[];
  recentEntityResults: RecentEntitySelection[];
  entityResults: SearchEntity[];
  visibleEntityDestinations: SearchEntityDestination[];
  query: string;
  canShowMoreEntityResults: boolean;
};

export function buildGlobalSearchGroups({
  branchId,
  selectedModule,
  selectedEntity,
  frequentModules,
  frequentEntities,
  visibleModules,
  recentEntityResults,
  entityResults,
  visibleEntityDestinations,
  query,
  canShowMoreEntityResults,
}: BuildGlobalSearchGroupsInput): SearchResultGroup[] {
  const groups: SearchResultGroup[] = [];

  if (!selectedModule) {
    if (frequentModules.length > 0) {
      groups.push({
        id: "frequent-modules",
        heading: "Frequent modules",
        items: frequentModules.map((module) => ({
          kind: "module",
          id: `frequent-${module.id}`,
          value: `frequent-module-${module.id}`,
          label: module.label,
          description: module.description,
          module,
        })),
      });
    }

    if (frequentEntities.length > 0) {
      groups.push({
        id: "frequent-entities",
        heading: "Frequent entities",
        items: frequentEntities.map(({ module, entity, count }) => ({
          kind: "frequentEntity",
          id: `frequent-entity-${module.id}-${entity.id}`,
          value: `frequent-entity-${module.id}-${entity.id}`,
          label: entity.name || "Untitled",
          description: `${module.label} · Opened ${count} times`,
          module,
          entity,
        })),
      });
    }

    groups.push({
      id: "modules",
      heading: "Modules",
      items: visibleModules.map((module) => ({
        kind: "module",
        id: `module-${module.id}`,
        value: `module-${module.id}`,
        label: module.label,
        description: module.description,
        module,
      })),
    });

    if (branchId) {
      groups.push({
        id: "quick-pages",
        heading: "Quick pages",
        items: visibleModules.slice(0, 3).map((module) => ({
          kind: "page",
          id: `page-${module.id}`,
          value: `page-${module.id}`,
          label: `Go to ${module.label}`,
          description: module.description,
          moduleId: module.id,
          href: module.landingHref(branchId),
        })),
      });
    }

    if (query) {
      groups.push({
        id: "actions",
        heading: "Actions",
        items: [
          {
            kind: "action",
            id: "action-clear-query",
            value: "action-clear-query",
            label: "Clear query",
            description: "Reset current search text",
            actionId: "clear-query",
          },
        ],
      });
    }

    return groups;
  }

  if (selectedModule && !selectedEntity) {
    groups.push({
      id: "module-open",
      heading: "Module",
      items: [
        {
          kind: "moduleOpen",
          id: `open-${selectedModule.id}`,
          value: `open-${selectedModule.id}`,
          label: `Open ${selectedModule.label}`,
          description: selectedModule.description,
          module: selectedModule,
        },
      ],
    });
  }

  if (selectedModule && !selectedEntity && recentEntityResults.length > 0) {
    groups.push({
      id: "recent-entities",
      heading: "Recent",
      items: recentEntityResults.map((selection) => ({
        kind: "recentEntity",
        id: `recent-${selection.moduleId}-${selection.entityId}`,
        value: `recent-${selection.moduleId}-${selection.entityId}`,
        label: selection.label,
        description: "Recently opened",
        module: selectedModule,
        entity: {
          id: selection.entityId,
          name: selection.label,
        },
      })),
    });
  }

  if (selectedModule && !selectedEntity && entityResults.length > 0) {
    groups.push({
      id: "entities",
      heading: "Entities",
      items: [
        ...entityResults.map((entity) => ({
          kind: "entity" as const,
          id: `entity-${selectedModule.id}-${entity.id}`,
          value: `entity-${selectedModule.id}-${entity.id}`,
          label: entity.name || "Untitled",
          description: `Open ${selectedModule.label} item`,
          module: selectedModule,
          entity,
        })),
        ...(canShowMoreEntityResults
          ? [
              {
                kind: "hint" as const,
                id: `more-${selectedModule.id}`,
                value: `more-${selectedModule.id}`,
                label: "Scroll to load more",
                disabled: true as const,
                className: "justify-center text-xs text-muted-foreground",
              },
            ]
          : []),
      ],
    });
  }

  if (selectedModule && selectedEntity && visibleEntityDestinations.length > 0) {
    groups.push({
      id: "destinations",
      heading: "Go to",
      items: visibleEntityDestinations.map((destination) => ({
        kind: "destination",
        id: `destination-${selectedModule.id}-${selectedEntity.id}-${destination.id}`,
        value: `destination-${selectedModule.id}-${selectedEntity.id}-${destination.id}`,
        label: destination.label,
        description: destination.description,
        module: selectedModule,
        entity: selectedEntity,
        destination,
      })),
    });
  }

  groups.push({
    id: "actions",
    heading: "Actions",
    items: [
      {
        kind: "action",
        id: "action-open-module-landing",
        value: "action-open-module-landing",
        label: `Open ${selectedModule.label} landing page`,
        description: "Navigate to module root page",
        actionId: "open-module-landing",
      },
      ...(selectedEntity
        ? [
            {
              kind: "action" as const,
              id: "action-clear-selected-entity",
              value: "action-clear-selected-entity",
              label: "Clear selected entity",
              description: "Go back to entity list",
              actionId: "clear-selected-entity",
            },
          ]
        : []),
      {
        kind: "action",
        id: "action-clear-search-state",
        value: "action-clear-search-state",
        label: "Clear search state",
        description: "Reset selected module, entity, and query",
        actionId: "clear-search-state",
      },
    ],
  });

  if (!query && selectedModule && selectedEntity && groups.length === 0) {
    return [];
  }

  return groups;
}
